export const ITEM_CREATION = `OUTPUT REQUIREMENT: Your entire response must be a single, valid JSON object. Do not include any text, notes, or markdown outside of the JSON structure.
    ALWAYS return items in this format: {"items": [array of item objects]}
    Even for a single item, wrap it in the items array
    
    INPUTS:
    base_item : {{item}} // JSON of the seed question (MCQ or Cloze)
    question_type : {{questionType}}
    variant_count : {{variantCount}}
    
    GOAL:
    Create {{variantCount}} realistic variants of the base_item.
    
    OUTPUT FORMAT (ALWAYS):
    {
      "items": [
        {
          "type": "mcq",
          "stimulus": "Question text here",
          "options": [...],
          "validation": {...}
        }
        // ... more items if variantCount > 1
      ]
    }
    
    RULES:
    Item-Type Fidelity :
      If question_type === "mcq", every variant must be an MCQ with exactly four options.
      If question_type === "clozeformula", every variant must be a single-blank fill-in-the-blank.
    CRITICAL RULE: CONTEXT PRESERVATION
      Identify the professional/career context from base_item (e.g., nursing, electrician, pharmacist).
      Every generated variant must remain in this exact same context.
      You are not allowed to switch to a different domain (e.g., do not switch nursing → construction).
      Expansion or rewording must always stay within this career scenario.
      Violating this constraint invalidates the output.
    Use Case Alignment
      The real-world context or professional application in the original question must be preserved across all variants.
      Do not change the field, scenario type, or user persona implied in the original question (e.g., if it is about a pharmacist measuring dosage, the variant must also be in a dosage context).
    Concept & Difficulty Preservation
      Maintain the same academic concept and cognitive demand as the base_item.
      Do not add or remove solution steps; the core mathematical structure must be identical.
      The level of difficulty must remain consistent with the original question.
    Content Variation:
      Rewrite the question using different numerical values.
      If the base_item is vague, under-specified, or missing parts (e.g., only a description or poorly worded prompt), infer the intended question structure and complete it logically and clearly based on the academic concept being tested, without changing career context.
      Add distractors that are plausible and relevant to the context. Also provide explanations provided for why incorrect answers are wrong, but only in the distractors
      If necessary, expand the stimulus to create a self-contained and coherent item without introducing new academic content.
      The new version must match the tone, complexity, and structure of the intended original question—even if the base was incomplete or unclear.
      Shuffle the order of answer options in MCQs to ensure no positional bias.
      Do not introduce or remove steps in the reasoning or change the question type.
      Maintain clarity and instructional quality throughout
      Change numerical values, keeping the same relationships between them. Use LaTeX for all math expressions.
      Rephrase wording to enhance novelty without altering meaning or difficulty.
    Answer Integrity
      Recompute the correct answer(s) for each variant based on the updated values.
      Shuffle the order of answer options in MCQs to ensure no positional bias.
      For MCQs, keep the same number of distractors, and ensure all options remain plausible and consistent with the modified scenario.
    Handling Negative Values
      Use negative values only where logically appropriate (e.g., loss, decrease, temperature drop).
      If the original math leads to a negative result, recast the scenario so that a negative value makes contextual sense.
      Do not assign negative signs to inherently positive quantities like time, distance, or quantity unless representing a change. 
    JSON & LaTeX Formatting (Strict): Even if the original question was not in LaTeX
      Make sure all equations, fractions, exponents, number-units are in LaTeX
      All keys and string values must be in double quotes (").
      Use &lt; and &gt; for < and > symbols.
      For all other special characters, ensure they are properly escaped.
      Don't use dollar signs for math mode, use LaTeX syntax directly: use \\(...\\) for inline math
      Represent currency as plain text (e.g., "$500") outside of LaTeX.
      Don't make anything bold, or add headings, even if it already was
    
    CRITICAL OUTPUT RULES:
    ALWAYS return {"items": [array]} format, even for single items
    Never return comma-separated JSON objects
    Always ensure the output is a single, parseable JSON structure
    The "items" array should contain exactly {{variantCount}} item objects

    
    IMPORTANT:
    The final output must be a valid JSON object beginning with { and ending with }.
    Verify your JSON is valid before returning - no trailing commas, proper quote usage, etc.`;

export const CREATE_ITEM_VALIDATION = `**Learnosity Validation Check**
    You are a question validator. Check if the created question:

    1. Keeps the original math/concept intact
    2. Has clear wording and correct math
    3. Explains wrong answers properly

    **Question Type:** {{questionType}}

    **Original Core Concept:**
    {{originalItem}}

    **Customized Question:**
    {{generatedItem}}

    **Check For:**
    - Math errors in question/answers
    - Wrong explanations for answer choices
    - Grammar/spelling mistakes
    - Career terms that don't fit
    - Changes to original math concept

    **Response Rules:**
    - Return only a valid JSON without markdown
    - Write issues in simple language
    - 1 line per issue (max 15 words)
    - No technical terms
    - Only report real problems

    **IMPORTANT:**
    - Ignore if no explanation is provided for incorrect answers
    - Ignore if Original core concept is missing for comparison

    DO NOT:
    - Return anything except JSON
    - Use markdown/formatting
    - Mention technical terms like "JSON" or "array"
    - Validate real-world practicality

    **Examples of Valid Issues:**
    - "Truck mechanic term used but question about cars"
    - "Option 2's explanation mentions wrong calculation"

    **Valid Response Format:**
    { "issues": ["issue1", "issue2"] } 
    { "issues": [] } if no problems

    Your Response MUST BE valid JSON starting with { and ending with }`;

export const ITEM_CREATION_FROM_PROMPT = `OUTPUT REQUIREMENT: Your entire response must be a single, valid JSON object. Do not include any text, notes, or markdown outside of the JSON structure.
    ALWAYS return items in this format: {"items": [array of item objects]}
    
    INPUTS:
    numberOfQuestions: {{numberOfQuestions}}
    {{contextInput}}
    competencyName: {{competencyName}}
    microcompetencyName: {{microcompetencyName}}
    grade: {{grade}}
    bloomLevel: {{bloomLevel}}
    dok: {{dok}}
    difficulty: {{difficulty}}
    
    GOAL:
    Create {{numberOfQuestions}} realistic multiple-choice questions (MCQs) based on the provided inputs.
    
    OUTPUT FORMAT (ALWAYS):
    {
      "items": [
        {
          "type": "mcq",
          "stimulus": "Question text here",
          "options": [
            { "label": "Option A", "value": "0", "is_correct": false, "rationale": "..." },
            { "label": "Option B", "value": "1", "is_correct": true, "rationale": "..." },
            ...
          ],
          "validation": {...}
        }
        // ... more items
      ]
    }
    
    RULES:
    Context & Relevance:
      {{contextRelevance}}
      Focus specifically on the micro-competency "{{microcompetencyName}}".
      Ensure the content is appropriate for {{grade}}.
      Align the cognitive level with Bloom's Taxonomy: {{bloomLevel}} and Depth of Knowledge (DOK): {{dok}}.
      The difficulty level should be {{difficulty}}.

    Item Structure:
      Each item must be a Multiple Choice Question (MCQ) with exactly 4 options.
      One option must be correct, and three must be plausible distractors.
      Provide a clear rationale for why the correct answer is correct and why the distractors are incorrect (in the distractors' rationale).

    Content Quality:
      Ensure the questions are clear, unambiguous, and free of bias.
      Use realistic scenarios where applicable.
      Avoid negative phrasing unless necessary.
      Audience & names: Write for US learners (US customary units, USD, US spelling when units or money apply, unless the competency explicitly requires metric or international context). Use varied, culturally diverse person names across questions; do not reuse the same placeholder names (e.g., John, Mary) in every item.
      
    JSON & LaTeX Formatting (Strict):
      Make sure all equations, fractions, exponents, number-units are in LaTeX
      All keys and string values must be in double quotes (").
      Use &lt; and &gt; for < and > symbols.
      For all other special characters, ensure they are properly escaped.
      Don't use dollar signs for math mode, use LaTeX syntax directly: use "\\(...\\)" for inline math. Make sure to usr this exact syntax
      Represent currency as plain text (e.g., "$500") outside of LaTeX.
      Don't make anything bold, or add headings, even if it already was
    
    CRITICAL OUTPUT RULES:
    ALWAYS return {"items": [array]} format.
    The "items" array should contain exactly {{numberOfQuestions}} item objects.
    The final output must be a valid JSON object beginning with { and ending with }.`;

export const CREATE_PROMPT_VALIDATION_ITEM = `**Learnosity Validation Check**
    You are a question validator. Check if the created question:

    1. Is relevant to the {{contextLabel}} "{{contextValue}}" and competency "{{competencyName}}".
    2. Addresses the micro-competency "{{microcompetencyName}}".
    3. Is appropriate for {{grade}} and {{difficulty}} level.
    4. The answer is correct
    5. Has clear wording and correct content.
    6. Explains wrong answers properly.
    7. Is realistic

    **Question Type:** MCQ

    **Created Question:**
    {{generatedItem}}

    **Check For:**
    - Content errors
    - Wrong explanations for answer choices
    - Grammar/spelling mistakes
    - Context terms that don't fit (career or interest)
    - Mismatch with requested grade/difficulty/competency

    **Response Rules:**
    - Return only a valid JSON without markdown
    - Write issues in simple language
    - 1 line per issue (max 15 words)
    - No technical terms
    - Only report real problems

    DO NOT:
    - Return anything except JSON
    - Use markdown/formatting
    - Mention technical terms like "JSON" or "array"
    - Validate real-world practicality

    **Valid Response Format:**
    { "issues": ["issue1", "issue2"] } 
    { "issues": [] } if no problems

    Your Response MUST BE valid JSON starting with { and ending with }`;
